import React, { useMemo } from 'react';
import { Client, decorateClient } from '@/db/types/client';
import ClickToCopy from './ClickToCopy';
import { titleize } from '../lib/inflection';
import { formatPhoneDisplay } from '@/lib/format-phone-display';
import { CalendarIcon, PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';

interface ClientInfoBarProps {
  client: Client;
  className?: string;
}

function calculateAge(dob: string): number | null {
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function formatDate(date: string | undefined) {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

function InfoChip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm text-[#555] whitespace-nowrap">
      <span className="text-gray-400">{icon}</span>
      {children}
    </span>
  );
}


export default function ClientInfoBar({ client, className = '' }: ClientInfoBarProps) {
  const dc = useMemo(() => decorateClient(client), [client]);
  const person = dc.person;

  const phone = person.phone_numbers?.find(p => p.primary) || person.phone_numbers?.[0];
  const dob = person.date_of_birth;
  const age = dob ? calculateAge(dob) : null;
  const address = client.address;
  const addressStr = address
    ? [address.address_line1, address.city, address.state, address.zip].filter(Boolean).join(', ')
    : null;

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    paused: 'bg-yellow-100 text-yellow-800',
    closed: 'bg-red-100 text-red-800',
  };

  return (
    <div className={`${className}`}>
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-[#fbf4f1] flex items-center justify-center text-sm font-bold text-[#dd8f75] shrink-0">
          {dc.initials}
        </div>

        <div className="flex-1 min-w-0">
          {/* Top row: name, ID, status */}
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{dc.person.displayName}</h1>
            <ClickToCopy
              text={dc.label}
              displayText={dc.label}
              showIconOnHover={false}
              className="text-xs font-semibold text-gray-900 bg-gray-100 border border-[#ddd] rounded-full px-2.5 py-0.5 hover:bg-gray-200"
            />
            {dc.status && (
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded ${statusColors[dc.status.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
                {dc.status.toLowerCase() === 'active' && <span className="w-2 h-2 rounded-full bg-green-500" />}
                {titleize(dc.status)}
              </span>
            )}
          </div>

          {/* Bottom row: contact details */}
          <div className="flex items-center gap-4 mt-1.5 flex-wrap">
            {dob && (
              <InfoChip icon={<CalendarIcon className="w-4 h-4" />}>
                {formatDate(dob)}{age !== null ? ` (age: ${age})` : ''}
              </InfoChip>
            )}
            {phone && (
              <InfoChip icon={<PhoneIcon className="w-4 h-4" />}>
                {formatPhoneDisplay(phone.number)}{phone.label ? ` (${phone.label})` : ''}
              </InfoChip>
            )}
            {person.email && (
              <InfoChip icon={<EnvelopeIcon className="w-4 h-4" />}>
                {person.email}
              </InfoChip>
            )}
            {addressStr && (
              <InfoChip icon={<MapPinIcon className="w-4 h-4" />}>
                {addressStr}
              </InfoChip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
